import { z } from 'zod';
import {
  ITEM_CONDITIONS,
  LIMITS,
  LOCALES,
  PRICE_TYPES,
  REPORT_REASONS,
  SORT_OPTIONS,
  type AttributeType,
} from './constants';
import { normalizeUzPhone } from './contacts';
import { validatePrice } from './money';

/**
 * Схемы валидации, общие для Mini App и API.
 * Ключи ошибок (message) — это ключи локализации `validation.*`.
 */

export const idSchema = z.string().regex(/^\d{1,19}$/, 'validation.invalid_id');

/** Цена в целых сумах, строкой (поддерживает пробелы-разделители). */
export const priceSumSchema = z
  .string()
  .transform((s) => s.replace(/[\s _]/g, ''))
  .refine((s) => s === '' || /^\d{1,13}$/.test(s), 'validation.price_invalid')
  .refine((s) => s === '' || BigInt(s) <= BigInt(LIMITS.maxPriceSum), 'validation.price_too_large');

const trimmed = (min: number, max: number, key: string) =>
  z
    .string()
    .transform((s) => s.replace(/\s+/g, ' ').trim())
    .pipe(z.string().min(min, `validation.${key}_too_short`).max(max, `validation.${key}_too_long`));

export const attributeValueSchema = z.union([z.string().max(200), z.number().finite(), z.boolean()]);

/** Черновик: все поля необязательны (автосохранение частично заполненной формы). */
export const listingDraftSchema = z.object({
  categoryId: idSchema.nullable().optional(),
  title: z.string().max(LIMITS.titleMax, 'validation.title_too_long').optional(),
  description: z.string().max(LIMITS.descriptionMax, 'validation.description_too_long').optional(),
  priceType: z.enum(PRICE_TYPES).optional(),
  priceSum: priceSumSchema.optional(),
  condition: z.enum(ITEM_CONDITIONS).nullable().optional(),
  locationId: idSchema.nullable().optional(),
  attributes: z.record(z.string().max(64), attributeValueSchema.nullable()).optional(),
});
export type ListingDraftInput = z.input<typeof listingDraftSchema>;

/** Полная проверка перед отправкой на модерацию/публикацией. */
export const listingSubmitSchema = z
  .object({
    categoryId: idSchema,
    title: trimmed(LIMITS.titleMin, LIMITS.titleMax, 'title'),
    description: trimmed(LIMITS.descriptionMin, LIMITS.descriptionMax, 'description'),
    priceType: z.enum(PRICE_TYPES),
    priceSum: priceSumSchema.optional(),
    condition: z.enum(ITEM_CONDITIONS).nullable().optional(),
    locationId: idSchema,
    attributes: z.record(z.string(), attributeValueSchema.nullable()).default({}),
  })
  .superRefine((v, ctx) => {
    const minor = v.priceSum ? BigInt(v.priceSum) * 100n : null;
    const err = validatePrice(v.priceType, minor);
    if (err) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['priceSum'], message: `validation.${err}` });
  });
export type ListingSubmitInput = z.input<typeof listingSubmitSchema>;

export const searchQuerySchema = z.object({
  q: z.string().max(LIMITS.searchQueryMax).optional(),
  categoryId: idSchema.optional(),
  locationId: idSchema.optional(),
  priceMin: priceSumSchema.optional(),
  priceMax: priceSumSchema.optional(),
  condition: z.enum(ITEM_CONDITIONS).optional(),
  withPhotos: z
    .union([z.boolean(), z.enum(['true', 'false', '1', '0'])])
    .transform((v) => v === true || v === 'true' || v === '1')
    .optional(),
  /** Только действующие VIP (блок «VIP» над выдачей); при выключенном продвижении — пустой ответ. */
  vipOnly: z
    .union([z.boolean(), z.enum(['true', 'false', '1', '0'])])
    .transform((v) => v === true || v === 'true' || v === '1')
    .optional(),
  /** Продавец: частное лицо или действующий бизнес-профиль. */
  sellerType: z.enum(['private', 'business']).optional(),
  /** Только «возможен торг». */
  negotiable: z
    .union([z.boolean(), z.enum(['true', 'false', '1', '0'])])
    .transform((v) => v === true || v === 'true' || v === '1')
    .optional(),
  sort: z.enum(SORT_OPTIONS).default('newest'),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(LIMITS.pageSizeMax).default(LIMITS.pageSizeDefault),
  /** Фильтры по атрибутам: attr.<key>=value или attr.<key>.min / .max */
  attrs: z.record(z.string().max(64), z.string().max(100)).optional(),
});
export type SearchQuery = z.infer<typeof searchQuerySchema>;

export const reportCreateSchema = z
  .object({
    listingId: idSchema.optional(),
    sellerId: idSchema.optional(),
    /** Жалоба из чата: продавец/покупатель-собеседник (sellerId) + переписка для модератора. */
    conversationId: idSchema.optional(),
    reason: z.enum(REPORT_REASONS),
    comment: z.string().max(LIMITS.reportCommentMax).optional(),
  })
  .refine((v) => Boolean(v.listingId) !== Boolean(v.sellerId), {
    message: 'validation.report_target_required',
    path: ['listingId'],
  })
  .refine((v) => !v.conversationId || Boolean(v.sellerId), {
    message: 'validation.report_target_required',
    path: ['sellerId'],
  })
  .refine((v) => v.reason !== 'other' || (v.comment?.trim().length ?? 0) >= 5, {
    message: 'validation.report_comment_required',
    path: ['comment'],
  });
export type ReportCreateInput = z.input<typeof reportCreateSchema>;

export const inquiryCreateSchema = z
  .object({
    listingId: idSchema,
    message: z.string().max(LIMITS.inquiryMessageMax).optional(),
    shareUsername: z.boolean().default(false),
    phone: z.string().max(30).optional(),
  })
  .refine((v) => !v.phone || normalizeUzPhone(v.phone) !== null, {
    message: 'validation.phone_invalid',
    path: ['phone'],
  });
export type InquiryCreateInput = z.input<typeof inquiryCreateSchema>;

export const contactPreferencesSchema = z
  .object({
    showTelegramUsername: z.boolean(),
    showPhone: z.boolean(),
    phone: z.string().max(30).nullable(),
    allowBotInquiries: z.boolean(),
    allowChat: z.boolean().optional(),
  })
  .refine((v) => !v.showPhone || normalizeUzPhone(v.phone) !== null, {
    message: 'validation.phone_invalid',
    path: ['phone'],
  });
export const chatStartSchema = z.object({ listingId: idSchema });
export const chatMessageSchema = z.object({
  /** До очистки; окончательная длина проверяется normalizeChatText (не более LIMITS.chatMessageMax символов). */
  text: z.string().min(1).max(LIMITS.chatMessageMax * 2),
});
export const chatReadSchema = z.object({ upToId: idSchema });
export const promoteSchema = z.object({ tariffCode: z.string().regex(/^[a-z0-9_]{2,32}$/) });

export type ContactPreferencesInput = z.input<typeof contactPreferencesSchema>;

export const userSettingsSchema = z.object({
  locale: z.enum(LOCALES).optional(),
  locationId: idSchema.nullable().optional(),
  notifyListingStatus: z.boolean().optional(),
  notifyInquiries: z.boolean().optional(),
  onboardingCompleted: z.boolean().optional(),
});
export type UserSettingsInput = z.input<typeof userSettingsSchema>;

export const rejectSchema = z.object({
  reasonCode: z.enum(['prohibited', 'bad_photos', 'wrong_category', 'incomplete', 'duplicate', 'fraud_suspected', 'other']),
  reasonText: z.string().max(500).optional(),
});
export type RejectInput = z.infer<typeof rejectSchema>;

// ---------- Динамические атрибуты категорий ----------

export interface AttributeDefinition {
  key: string;
  type: AttributeType;
  required: boolean;
  options?: string[] | null;
  min?: number | null;
  max?: number | null;
}

/**
 * Проверка значений атрибутов по определениям категории.
 * Возвращает словарь key → ключ ошибки локализации. Неизвестные ключи — ошибка.
 */
export function validateAttributeValues(
  defs: AttributeDefinition[],
  values: Record<string, string | number | boolean | null | undefined>,
): Record<string, string> {
  const errors: Record<string, string> = {};
  const byKey = new Map(defs.map((d) => [d.key, d]));
  for (const key of Object.keys(values)) {
    if (!byKey.has(key)) errors[key] = 'validation.attribute_unknown';
  }
  for (const def of defs) {
    const v = values[def.key];
    const empty = v === undefined || v === null || v === '';
    if (empty) {
      if (def.required) errors[def.key] = 'validation.required';
      continue;
    }
    switch (def.type) {
      case 'number': {
        const n = typeof v === 'number' ? v : Number(String(v).replace(/\s/g, ''));
        if (!Number.isFinite(n)) errors[def.key] = 'validation.number_invalid';
        else if (def.min != null && n < def.min) errors[def.key] = 'validation.number_too_small';
        else if (def.max != null && n > def.max) errors[def.key] = 'validation.number_too_large';
        break;
      }
      case 'boolean':
        if (typeof v !== 'boolean') errors[def.key] = 'validation.boolean_invalid';
        break;
      case 'select':
        if (typeof v !== 'string' || !(def.options ?? []).includes(v)) errors[def.key] = 'validation.option_invalid';
        break;
      case 'text':
        if (typeof v !== 'string' || v.trim().length > 200) errors[def.key] = 'validation.text_invalid';
        break;
    }
  }
  return errors;
}
